<template>
    <div class="chunk-uploader">
        <!-- 文件选择区域 -->
        <div class="upload-area">
            <el-button type="primary" @click="selectFile" :disabled="disabled">
                <i class="el-icon-upload"></i>
                {{ buttonText }}
            </el-button>
            <input ref="fileInput" type="file" style="display: none" @change="handleFileSelect" :accept="acceptTypes"
                :multiple="multiple">
        </div>

        <!-- 文件列表 -->
        <div v-if="fileList.length > 0" class="file-list">
            <el-table :data="fileList" style="width: 100%" size="small">
                <el-table-column prop="fileName" label="文件名" min-width="200">
                    <template slot-scope="scope">
                        <div class="file-name">
                            <i :class="getFileIcon(scope.row.fileType)"></i>
                            <span>{{ scope.row.fileName }}</span>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column prop="fileType" label="文件类型" width="100"></el-table-column>
                <el-table-column prop="fileSize" label="文件大小" width="120">
                    <template slot-scope="scope">
                        {{ formatFileSize(scope.row.fileSize) }}
                    </template>
                </el-table-column>
                <el-table-column prop="uploadTime" label="上传时间" width="150"></el-table-column>
                <el-table-column label="状态" width="120">
                    <template slot-scope="scope">
                        <el-tag :type="getStatusType(scope.row.status)" size="small">
                            {{ getStatusText(scope.row.status) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="进度" width="200" v-if="showProgress">
                    <template slot-scope="scope">
                        <div v-if="scope.row.status === 'uploading'" class="progress-container">
                            <el-progress :percentage="scope.row.progress || 0" :stroke-width="6"
                                :show-text="false"></el-progress>
                            <div class="progress-text">
                                {{ scope.row.progress || 0 }}%
                                <span
                                    v-if="scope.row.currentChunkIndex !== undefined && scope.row.uploadUrlsLength !== undefined">
                                    ({{ scope.row.currentChunkIndex + 1 }}/{{ scope.row.uploadUrlsLength }})
                                </span>
                            </div>
                        </div>
                        <span v-else-if="scope.row.status === 'completed'" class="success-text">100%</span>
                        <span v-else class="default-text">-</span>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="150">
                    <template slot-scope="scope">
                        <el-button v-if="scope.row.status === 'pending'" type="text" size="small"
                            @click="startUpload(scope.row)">
                            开始上传
                        </el-button>
                        <el-button v-if="scope.row.status === 'uploading'" type="text" size="small" disabled>
                            上传中...
                        </el-button>
                        <el-button v-if="scope.row.status === 'failed'" type="text" size="small"
                            @click="retryUpload(scope.row)">
                            重试
                        </el-button>
                        <el-button v-if="scope.row.status !== 'uploading'" type="text" size="small"
                            @click="removeFile(scope.$index,scope.row)" style="color: #f56c6c;">
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>

    </div>
</template>

<script>
import { doFileUpload } from '@/utils/upload/fileUploadHelper'
import { deleteBidAtt } from '@/api/modules/make'    
export default {
    name: 'ChunkUploader',
    props: {
        // 业务类型
        business: {
            type: String,
            default: 'tender'
        },

        fileGuid: {
            type: String,
            default: ''
        },

        fileBindId: {
            type: String,
            required: true
        },

        // 功能类型
        function: {
            type: String,
            default: 'attachment'
        },
        // 项目代码
        projectCode: {
            type: String,
            default: ''
        },
        // 允许的文件类型
        acceptTypes: {
            type: String,
            default: '.jpg,.zip,.rar,.pdf,.doc,.xls,.docx'
        },
        // 是否支持多选
        multiple: {
            type: Boolean,
            default: false
        },
        // 按钮文本
        buttonText: {
            type: String,
            default: '选取文件'
        },
        // 是否禁用
        disabled: {
            type: Boolean,
            default: false
        },
        // 是否显示进度条
        showProgress: {
            type: Boolean,
            default: true
        },
        // 最大文件大小（MB）
        maxFileSize: {
            type: Number,
            default: 100
        },
        // 是否自动上传
        autoUpload: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            fileList: [],
            uploadTasks: [],
            progressDialogVisible: false
        }
    },
    methods: {
        getEffectiveFileBindId() {
            return this.fileBindId || this.fileGuid || ''
        },

        // 选择文件
        selectFile() {
            this.$refs.fileInput.click()
        },

        // 处理文件选择
        async handleFileSelect(event) {
            const files = Array.from(event.target.files)
            for (const file of files) {
                await this.addFile(file)
            }
            // 清空input
            event.target.value = ''
        },

        // 添加文件到列表
        async addFile(file) {
            // 检查文件类型
            if (!this.checkFileType(file)) {
                return
            }

            // 检查文件大小
            if (!this.checkFileSize(file)) {
                return
            }

            const fileInfo = {
                fileName: file.name,
                fileType: this.getFileExtension(file.name).substring(1).toUpperCase(),
                fileSize: file.size,
                uploadTime: new Date().toLocaleString(),
                file: file,
                status: 'pending',
                progress: 0,
                projectCode: this.projectCode,
                uploadId: '',
                fileId: '',
                fileUrl: '',
                error: '',
                currentChunkIndex: 0,
                uploadUrlsLength: 0
            }

            this.fileList.push(fileInfo)

            // 自动上传
            if (this.autoUpload) {
                await this.startUpload(fileInfo)
            }

            this.$emit('file-added', fileInfo)
        },

        // 检查文件类型
        checkFileType(file) {
            const allowedTypes = this.acceptTypes.split(',').map(type => type.trim().toLowerCase())
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase()

            if (!allowedTypes.includes(fileExtension)) {
                this.$message.error(`文件类型不支持，请选择 ${this.acceptTypes} 格式的文件`)
                return false
            }
            return true
        },

        // 检查文件大小
        checkFileSize(file) {
            const maxSize = this.maxFileSize * 1024 * 1024
            if (file.size > maxSize) {
                this.$message.error(`文件大小不能超过${this.maxFileSize}MB`)
                return false
            }
            return true
        },

        // 获取文件扩展名
        getFileExtension(fileName) {
            return '.' + fileName.split('.').pop().toLowerCase()
        },

        // 开始上传
        async startUpload(fileInfo) {
            try {
                const fileBindId = this.getEffectiveFileBindId()
                if (!fileBindId) {
                    this.$message.error('缺少 fileBindId，无法上传')
                    return
                }

                // 更新状态
                const fileIndex = this.fileList.findIndex(f => f.fileName === fileInfo.fileName)
                if (fileIndex !== -1) {
                    this.fileList[fileIndex].status = 'uploading'
                    this.fileList[fileIndex].progress = 0
                }

                // 显示进度弹窗
                this.showProgressDialog()

                const uploadResult = await doFileUpload(fileInfo.file, {
                    fileBindId,
                    onProgress: (percentage) => {
                        const p = Math.max(0, Math.min(100, Number(percentage) || 0))
                        if (fileIndex !== -1) {
                            this.fileList[fileIndex].progress = Math.round(p)
                        }
                        this.$emit('upload-progress', {
                            fileInfo,
                            progress: Math.round(p)
                        })
                    },
                    onSuccess: (res) => {
                        if (fileIndex !== -1) {
                            this.fileList[fileIndex].status = 'completed'
                            this.fileList[fileIndex].progress = 100
                            this.fileList[fileIndex].fileGuid = res.fileGuid || ''
                            this.fileList[fileIndex].uploadFileId = res.uploadFileId || ''
                            this.fileList[fileIndex].filePath = res.filePath || ''
                            this.fileList[fileIndex].fileUrl = res.filePath || ''
                            this.fileList[fileIndex].fileId = res.fileId || res.uploadFileId || ''
                        }
                        this.$emit('upload-success', {
                            fileInfo: this.fileList[fileIndex],
                            result: res
                        })
                    },
                    onError: (fail) => {
                        const message = fail?.message || '上传失败'
                        if (fileIndex !== -1) {
                            this.fileList[fileIndex].status = 'failed'
                            this.fileList[fileIndex].error = message
                        }
                        this.$emit('upload-error', {
                            fileInfo,
                            error: fail
                        })
                    }
                })

                return uploadResult

            } catch (error) {
                console.error('上传异常:', error)

                // 更新状态
                const fileIndex = this.fileList.findIndex(f => f.fileName === fileInfo.fileName)
                if (fileIndex !== -1) {
                    this.fileList[fileIndex].status = 'failed'
                    this.fileList[fileIndex].error = error.message
                }

                this.$emit('upload-error', {
                    fileInfo,
                    error
                })
            }
        },

        // 重试上传
        async retryUpload(fileInfo) {
            await this.startUpload(fileInfo)
        },

        // 删除文件
        removeFile(index,row) {
            this.deleteAndDecryptAtt(index,row)
        },


           deleteAndDecryptAtt(index, row) {
      this.$confirm(`确定要删除附件 "${row.fileName}" 吗？`, '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteBidAtt(row.fileId)
          if (res.code === 200) {
            const fileInfo = this.fileList[index]
            this.fileList.splice(index, 1)
            this.$emit('file-removed', fileInfo)
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除附件失败:', error)
          this.$message.error('删除失败：' + (error.message || '未知错误'))
        }
      }).catch(() => {
        // 用户取消删除
      })
    },

        // 显示进度弹窗
        showProgressDialog() {
            this.progressDialogVisible = true
        },

        // 更新进度弹窗
        updateProgressDialog(task) {
            if (this.$refs.progressDialog) {
                this.$refs.progressDialog.updateTask(task)
            }
        },

        // 获取文件图标
        getFileIcon(fileType) {
            const iconMap = {
                'PDF': 'el-icon-document',
                'DOC': 'el-icon-document',
                'DOCX': 'el-icon-document',
                'XLS': 'el-icon-document',
                'XLSX': 'el-icon-document',
                'JPG': 'el-icon-picture',
                'JPEG': 'el-icon-picture',
                'PNG': 'el-icon-picture',
                'ZIP': 'el-icon-folder',
                'RAR': 'el-icon-folder'
            }
            return iconMap[fileType] || 'el-icon-document'
        },

        // 获取状态类型
        getStatusType(status) {
            const typeMap = {
                'pending': 'info',
                'uploading': 'warning',
                'completed': 'success',
                'failed': 'danger'
            }
            return typeMap[status] || 'info'
        },

        // 获取状态文本
        getStatusText(status) {
            const textMap = {
                'pending': '待上传',
                'uploading': '上传中',
                'completed': '上传完成',
                'failed': '上传失败'
            }
            return textMap[status] || '未知状态'
        },

        // 格式化文件大小
        formatFileSize(bytes) {
            if (bytes === 0) return '0 B'
            const k = 1024
            const sizes = ['B', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
        },

        // 获取已完成的文件
        getCompletedFiles() {
            return this.fileList.filter(file => file.status === 'completed')
        },

        // 获取上传中的文件
        getUploadingFiles() {
            return this.fileList.filter(file => file.status === 'uploading')
        },

        // 获取失败的文件
        getFailedFiles() {
            return this.fileList.filter(file => file.status === 'failed')
        },

        // 清空文件列表
        clearFiles() {
            this.fileList = []
        },

        // 重置组件
        reset() {
            this.clearFiles()
            this.progressDialogVisible = false
        }
    }
}
</script>

<style lang="less" scoped>
.chunk-uploader {
    .upload-area {
        margin-bottom: 20px;
        text-align: left;

        .el-button {
            background: #409eff;
            border-color: #409eff;
        }
    }

    .file-list {
        .file-name {
            display: flex;
            align-items: center;
            gap: 8px;

            i {
                font-size: 16px;
                color: #409eff;
            }
        }

        .success-text {
            color: #67c23a;
            font-weight: bold;
        }

        .default-text {
            color: #909399;
        }

        .progress-container {
            .el-progress {
                margin-bottom: 4px;

                .el-progress-bar__outer {
                    background-color: #f0f2f5;
                }
            }

            .progress-text {
                font-size: 12px;
                color: #606266;
                text-align: center;
                line-height: 1;
            }
        }
    }
}
</style>
